import { PolicyPageWrapper } from './PolicyPageWrapper'
import SeoHelmet from '../../components/common/SeoHelmet'

const ReturnPolicyPage = () => (
  <>
    <SeoHelmet
      title="Return Policy"
      description="Learn about Sokooni's return policy for marketplace purchases. Understand seller-specific policies and how to report issues with your orders."
      keywords="return policy, refunds, exchanges, marketplace returns, seller policies, Sokooni returns"
      url="/return-policy"
    />
    <PolicyPageWrapper title="Return Policy">
      <p>
        At Sokooni, we strive to ensure you have a safe and satisfying shopping experience. As a
        marketplace, each seller sets their own return policy. Here's what you need to know:
      </p>

      <h2 className="text-2xl font-semibold mt-4">1. Merchant-Specific Return Policies</h2>
      <p>
        Each product listing will clearly state the return policy set by the seller. If a product
        does not have a specific return policy listed, the default is:{' '}
        <strong>No returns or exchanges</strong>.
      </p>

      <h2 className="text-2xl font-semibold mt-4">2. Default Return Policy</h2>
      <p>
        Unless otherwise stated by the seller, all sales are final. However, you may be eligible for
        a return or replacement in the following cases:
      </p>
      <ul className="list-disc list-inside mb-6">
        <li>The item is defective or damaged upon delivery.</li>
        <li>You received the wrong item.</li>
        <li>The product is significantly different from what was described.</li>
      </ul>
      <p>
        In such cases, you must report the issue within <strong>48 hours</strong> of delivery by
        contacting Sokooni support at{' '}
        <a href="mailto:returns@sokooni.com" className="text-blue-600 hover:underline">
          returns@sokooni.com
        </a>
        .
      </p>

      <h2 className="text-2xl font-semibold mt-4">3. Conditions for Returns</h2>
      <ul className="list-disc list-inside mb-6">
        <li>The item must be unused and in the original packaging.</li>
        <li>You must provide proof of purchase (order ID or receipt).</li>
        <li>Photos or videos of the issue may be required.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-4">4. How to Request a Return</h2>
      <ol className="list-decimal list-inside mb-6">
        <li>
          Contact us at{' '}
          <a href="mailto:returns@sokooni.com" className="text-blue-600 hover:underline">
            returns@sokooni.com
          </a>{' '}
          or through the app.
        </li>
        <li>Include your order ID, item name, and reason for return.</li>
        <li>Our team will guide you through the next steps based on the seller’s policy.</li>
      </ol>

      <h2 className="text-2xl font-semibold mt-4">5. Refunds and Replacements</h2>
      <p>
        If approved, refunds will be issued via the original payment method within 7–14 business
        days. In some cases, you may receive a replacement item instead of a refund.
      </p>

      <h2 className="text-2xl font-semibold mt-4">6. Non-Returnable Items</h2>
      <ul className="list-disc list-inside mb-6">
        <li>Perishable goods (e.g., food, flowers).</li>
        <li>Personal care items (e.g., undergarments, beauty products).</li>
        <li>Digital downloads or services.</li>
        <li>Items marked “No Returns” on the product page.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-4">7. Disputes and Escalation</h2>
      <p>
        If you're unable to resolve a return issue with the seller, you can escalate the matter to
        Sokooni Support. We may intervene and mediate disputes at our discretion.
      </p>
    </PolicyPageWrapper>
  </>
)

export default ReturnPolicyPage
